import type { ReactNode } from "react";
import { useMemo } from "react";
import {
  Button,
  EmptyState,
  Flex,
  Heading,
  Icon,
  Icons,
  Modal,
  Pagination,
  Paragraph,
  Spin,
  Table,
  type TableColumnType,
} from "@ir/ui";
import { Permissions } from "@src/domain/permissions";
import HasAnyPermission from "@src/presentation/components/permissions/HasAnyPermission";
import { sumMasterDataColumnWidths } from "./masterDataTable.shared";

export interface MasterDataRetailerChildModalHostProps {
  isOpen: boolean;
  retailerId: number | null;
  retailerName: string;
  onClose: () => void;
}

interface MasterDataRetailerChildListModalProps<TRow extends { key: string }> {
  isOpen: boolean;
  title: string;
  addButtonLabel: string;
  emptyTitle: string;
  tableAriaLabel: string;
  paginationAriaLabel: string;
  loading: boolean;
  error: string | null;
  isFormOpen: boolean;
  formStrip: ReactNode;
  columns: TableColumnType<TRow>[];
  dataSource: TRow[];
  page: number;
  pageSize: number;
  itemTotal: number;
  onPageChange: (page: number) => void;
  onAdd: () => void;
  onClose: () => void;
  disableAdd?: boolean;
}

/**
 * Shared chrome for retailer child list modals (match rules, raw associations).
 */
export function MasterDataRetailerChildListModal<
  TRow extends { key: string },
>({
  isOpen,
  title,
  addButtonLabel,
  emptyTitle,
  tableAriaLabel,
  paginationAriaLabel,
  loading,
  error,
  isFormOpen,
  formStrip,
  columns,
  dataSource,
  page,
  pageSize,
  itemTotal,
  onPageChange,
  onAdd,
  onClose,
  disableAdd = false,
}: Readonly<MasterDataRetailerChildListModalProps<TRow>>) {
  const tableWidth = useMemo(
    () => sumMasterDataColumnWidths(columns),
    [columns],
  );
  const isAddDisabled = loading || isFormOpen || disableAdd;
  const showPagination = itemTotal > pageSize;

  let body: ReactNode = null;
  if (loading && dataSource.length === 0 && !isFormOpen) {
    body = (
      <Flex align="center" justifyContent="center" className="py-6">
        <Spin tip="Loading..." spinning />
      </Flex>
    );
  } else if (error) {
    body = (
      <>
        {formStrip}
        <Paragraph className="text-danger m-0">{error}</Paragraph>
      </>
    );
  } else if (dataSource.length === 0) {
    body = (
      <>
        {formStrip}
        <EmptyState
          className="bg-white border-1 border-color-grey-25 px-4 py-5 m-4"
          icon={Icons.InfoOctagon}
          title={
            <Heading level={6} className="text-grey-700 font-bold mt-3">
              {emptyTitle}
            </Heading>
          }
        />
      </>
    );
  } else {
    body = (
      <>
        {formStrip}
        <Table<TRow>
          columns={columns}
          dataSource={dataSource}
          rowHoverable
          aria-label={tableAriaLabel}
          sticky
          scroll={{ x: tableWidth, y: 360 }}
        />
      </>
    );
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      size="large"
      closable
      classNames={{
        body: "master-data-json-preview-modal__body",
      }}
      headerContent={
        <Flex
          align="center"
          justifyContent="space-between"
          gap={1}
          className="w-100"
        >
          <Heading level={6} className="text-grey-700 font-bold m-0">
            {title}
          </Heading>
          <HasAnyPermission requiredPermissions={[Permissions.MASTER_DATA_ADD]}>
            <Button
              size="small"
              variant="outline"
              onPress={onAdd}
              isDisabled={isAddDisabled}
            >
              <Icon slot="start" name={Icons.Plus} />
              {addButtonLabel}
            </Button>
          </HasAnyPermission>
        </Flex>
      }
      footerContent={
        <Flex
          align="center"
          justifyContent="center"
          className="w-100 mb-2"
          style={{ position: "relative" }}
        >
          {showPagination ? (
            <Pagination
              totalItems={itemTotal}
              itemsPerPage={pageSize}
              currentPage={page}
              onPageChange={onPageChange}
              isDisabled={loading}
              aria-label={paginationAriaLabel}
            />
          ) : null}
          <Button
            size="small"
            color="grey"
            onPress={onClose}
            style={{ position: "absolute", right: 0 }}
          >
            Close
          </Button>
        </Flex>
      }
    >
      {body}
    </Modal>
  );
}
